import React, { useCallback, useEffect, useState } from 'react';
import { Bot, Check, FileText, MessageSquare, RefreshCw, Send, Smartphone, Sparkles, UserPlus } from 'lucide-react';
import { ActionConfirmationModal } from './ActionConfirmationModal';
import { AITriageChips } from './AITriageChips';
import ChatViewHelpers from '../../admin/chat/ChatViewHelpers';
import ConversationThread from './ConversationThread';
import { CustomerNotAnsweringPanel, RecommendedActionCards } from './RecommendedActionCards';
import DirectParticipantChatCard from './DirectParticipantChatCard';
import { DriverIncidentPanel } from './DriverIncidentPanel';
import {
  acceptConversationAIDraft,
  buildCommunicationMessageFormData,
  dismissConversationAIDraft,
  executeConversationAction,
  fetchConversationWorkspace,
  generateConversationAIDraft,
  hasCommunicationMedia,
  improveConversationAIMessage,
  linkConversationDelivery,
  markProspectConverted,
  previewConversationAction,
  requireConversationHandoff,
  refreshConversationIntelligence,
  resolveConversationIdentity,
  sendConversationMessage,
  sendConversationWhatsAppMessage,
  sendConversationWhatsAppTemplate,
  sendProspectRegistrationLink,
  triggerProspectAutoReply,
  updateConversationAISettings,
  updateProspectLeadAIMode,
  updateProspectLeadStage,
} from './api';
import { socket } from '../../admin/chat/socket';
import MediaMessageControls from './MediaMessageControls';

const CHANNELS = [
  { id: 'internal', label: 'Interno', icon: MessageSquare },
  { id: 'whatsapp', label: 'WhatsApp', icon: Smartphone },
  { id: 'template', label: 'Plantilla', icon: Check },
  { id: 'note', label: 'Nota admin', icon: FileText },
];

function field(label, value) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="rounded-lg bg-slate-50 p-2">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-xs font-semibold text-slate-800">{value}</p>
    </div>
  );
}

export default function AdminConversationWorkspace({ conversation, userId }) {
  const [channel, setChannel] = useState('internal');
  const [draft, setDraft] = useState('');
  const [mediaAttachment, setMediaAttachment] = useState(null);
  const [aiState, setAIState] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [actionPreview, setActionPreview] = useState(null);
  const [confirmationPreview, setConfirmationPreview] = useState(null);
  const [routeContext, setRouteContext] = useState(null);
  const [directSupportTargets, setDirectSupportTargets] = useState([]);
  const [lead, setLead] = useState(null);
  const [aiDraftText, setAiDraftText] = useState('');
  const [templates, setTemplates] = useState([]);
  const [whatsappWindow, setWhatsappWindow] = useState(null);
  const [deliveryLinkId, setDeliveryLinkId] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executingAction, setExecutingAction] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [threadRefreshSignal, setThreadRefreshSignal] = useState(0);

  const conversationId = conversation?.id;
  const delivery = aiState?.context?.delivery || conversation?.delivery || {};
  const settings = aiState?.settings || {};
  const pendingDraft = aiState?.pending_draft;
  const participants = aiState?.context?.participants || conversation?.participants || [];
  const identity = aiState?.context?.identity || {};
  const incidentResolution = identity.driver_incident_resolution || {};
  const triage = intelligence?.triage || conversation?.triage || {};
  const hasWhatsAppWindowPolicy = whatsappWindow && Object.prototype.hasOwnProperty.call(whatsappWindow, 'freeform_available');
  const whatsappFreeformBlocked = Boolean(hasWhatsAppWindowPolicy && !whatsappWindow.freeform_available);
  const whatsappDisabledReason = whatsappWindow?.disabled_reason || 'La ventana WhatsApp de 24h esta cerrada. Usa una plantilla aprobada.';
  const recommendedActions = [
    ...(pendingDraft?.recommended_actions || []),
    ...((intelligence?.triage?.recommended_actions || []).filter(Boolean)),
  ];
  const latestIntent = settings.last_intent || pendingDraft?.intent || triage.latest_intent;

  const loadTools = useCallback(async () => {
    if (!conversationId) return;
    setLoadingAI(true);
    setError(null);
    try {
      const workspace = await fetchConversationWorkspace(conversationId);
      const ai = workspace.ai_state || {};
      const templateData = workspace.templates || {};
      setAIState(ai);
      setIntelligence(workspace.intelligence || null);
      setRouteContext(workspace.driver_route_context || null);
      setDirectSupportTargets(workspace.direct_support_targets || []);
      setLead(workspace.lead || null);
      setAiDraftText(ai.pending_draft?.draft_text || '');
      setTemplates(templateData.templates || []);
      setWhatsappWindow(templateData.whatsapp_window || null);
      if (templateData.whatsapp_window && templateData.whatsapp_window.freeform_available === false) {
        setChannel('template');
      }
    } catch (err) {
      console.error('Communication Hub tools error:', err);
      setError('No se pudieron cargar AI/templates de esta conversacion.');
      setAIState(null);
      setIntelligence(null);
      setRouteContext(null);
      setDirectSupportTargets([]);
      setLead(null);
      setTemplates([]);
      setWhatsappWindow(null);
    } finally {
      setLoadingAI(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setDraft('');
    setChannel('internal');
    setActionPreview(null);
    loadTools();
  }, [conversationId, loadTools]);

  useEffect(() => {
    if (!conversationId) return undefined;
    const handleAI = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      setAIState(prev => ({
        ...(prev || {}),
        settings: payload.settings || prev?.settings,
        pending_draft: payload.pending_draft ?? prev?.pending_draft,
        context: payload.context || prev?.context,
      }));
      if (payload.pending_draft) setAiDraftText(payload.pending_draft.draft_text || '');
    };
    const handleIntelligence = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      if (payload.intelligence) setIntelligence(payload.intelligence);
    };
    const handleAction = (payload) => {
      if (Number(payload?.conversation_id) !== Number(conversationId)) return;
      loadTools();
    };
    const handleLead = (payload) => {
      const nextLead = payload?.lead;
      if (!nextLead || Number(nextLead.conversation_id) !== Number(conversationId)) return;
      setLead(nextLead);
    };

    socket.on('connect', loadTools);
    socket.on('conversation_ai_state_updated', handleAI);
    socket.on('conversation_intelligence_updated', handleIntelligence);
    socket.on('conversation_action_completed', handleAction);
    socket.on('conversation_triage_updated', handleAction);
    socket.on('communication_lead_updated', handleLead);

    return () => {
      socket.off('connect', loadTools);
      socket.off('conversation_ai_state_updated', handleAI);
      socket.off('conversation_intelligence_updated', handleIntelligence);
      socket.off('conversation_action_completed', handleAction);
      socket.off('conversation_triage_updated', handleAction);
      socket.off('communication_lead_updated', handleLead);
    };
  }, [conversationId, loadTools]);

  async function handleSend() {
    const text = draft.trim();
    if ((!text && !mediaAttachment) || !conversationId || sending) return;
    setSending(true);
    setError(null);
    try {
      if (channel === 'whatsapp') {
        if (whatsappFreeformBlocked) {
          throw new Error(whatsappDisabledReason);
        }
        const payload = {
          content: text,
          message_type: mediaAttachment?.type || 'text',
          media_file: mediaAttachment?.file,
          media_type: mediaAttachment?.type,
        };
        await sendConversationWhatsAppMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
        );
        setDraft('');
        setMediaAttachment(null);
        setThreadRefreshSignal(value => value + 1);
        await loadTools();
      } else if (channel === 'template') {
        setError('Selecciona una plantilla aprobada desde el panel de templates.');
      } else if (channel === 'note' && mediaAttachment) {
        setError('Las notas admin no soportan imagen/audio en v1.');
      } else {
        const payload = {
          content: text,
          is_note: channel === 'note',
          message_type: mediaAttachment?.type || (channel === 'note' ? 'note' : 'text'),
          media_file: mediaAttachment?.file,
          media_type: mediaAttachment?.type,
        };
        await sendConversationMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
        );
        setDraft('');
        setMediaAttachment(null);
        setThreadRefreshSignal(value => value + 1);
        await loadTools();
      }
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.message || payload?.error || 'No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  }

  function handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (!draft.trim() && !mediaAttachment) return;
    handleSend();
  }

  async function handleTemplate(templateId) {
    setSending(true);
    setError(null);
    try {
      await sendConversationWhatsAppTemplate(conversationId, templateId);
      setThreadRefreshSignal(value => value + 1);
      await loadTools();
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.message || payload?.error || 'No se pudo enviar la plantilla.');
    } finally {
      setSending(false);
    }
  }

  async function handleGenerateDraft() {
    setLoadingAI(true);
    setError(null);
    try {
      const result = await generateConversationAIDraft(conversationId);
      setAIState(prev => ({ ...(prev || {}), pending_draft: result.draft }));
      setAiDraftText(result.draft?.draft_text || '');
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo generar borrador AI.');
    } finally {
      setLoadingAI(false);
    }
  }

  async function handleRefreshIntelligence() {
    setLoadingAI(true);
    setError(null);
    try {
      const result = await refreshConversationIntelligence(conversationId);
      setIntelligence(result);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo actualizar inteligencia.');
    } finally {
      setLoadingAI(false);
    }
  }

  async function handlePreviewAction(action) {
    const actionType = action?.type || action;
    if (!actionType) return;
    setLoadingPreview(true);
    setError(null);
    try {
      const payload = action?.payload ? { ...action.payload, ...action } : (action || {});
      const preview = await previewConversationAction(conversationId, actionType, payload);
      setActionPreview(preview);
    } catch (err) {
      if (
        (action && String(actionType).includes('customer_not_answering'))
        || ['acknowledge_driver_hold', 'reset_remaining_route_to_pending'].includes(actionType)
      ) {
        setActionPreview(buildLocalIncidentPreview(action));
      } else {
        setError(err?.response?.data?.error || 'No se pudo previsualizar la accion.');
      }
    } finally {
      setLoadingPreview(false);
    }
  }

  function buildLocalIncidentPreview(action) {
    const actionType = action?.type || action;
    const affectedDeliveryIds = [
      delivery?.id,
      ...((routeContext?.next_driver_deliveries || routeContext?.next_deliveries || [])
        .map(item => item.id || item.delivery_point_id)),
    ].filter(Boolean);
    const isReset = actionType === 'reset_remaining_route_to_pending';
    return {
      ok: true,
      conversation_id: conversationId,
      action_type: actionType,
      label: action?.label || actionType,
      reason: action?.reason || 'Cliente no contesta',
      preview_text: action?.reason || 'Preview generado localmente hasta que backend exponga preview específico.',
      payload: action?.payload || {},
      mutates: Boolean(action?.mutates || isReset),
      requires_admin_confirmation: true,
      affected_delivery_ids: isReset ? affectedDeliveryIds : [delivery?.id].filter(Boolean),
      status_changes: isReset
        ? affectedDeliveryIds.map(id => ({ delivery_id: id, from: 'current', to: 'pendiente' }))
        : [],
      warnings: ['backend_preview_unavailable', ...(isReset ? ['db_status_mutation'] : [])],
    };
  }

  function applyActionPreview() {
    if (!actionPreview) return;
    if (actionPreview.ui_action === 'prefill_composer') {
      setDraft(actionPreview.preview_text || '');
      setChannel(actionPreview.composer_channel || 'whatsapp');
    } else if (actionPreview.action_type === 'mark_handoff') {
      setConfirmationPreview(actionPreview);
    } else if (actionPreview.action_type === 'send_template') {
      setChannel('template');
      setConfirmationPreview(actionPreview);
    } else if (actionPreview.requires_admin_confirmation) {
      setConfirmationPreview(actionPreview);
    }
  }

  async function handleConfirmAction() {
    if (!confirmationPreview) return;
    setExecutingAction(true);
    setError(null);
    try {
      await executeConversationAction(
        conversationId,
        confirmationPreview.action_type,
        {
          ...(confirmationPreview.payload || {}),
          confirmed: true,
          preview_id: confirmationPreview.preview_id,
          reason: confirmationPreview.reason || confirmationPreview.payload?.reason || (confirmationPreview.action_type === 'reset_remaining_route_to_pending' ? 'customer_not_answering' : undefined),
          scope: confirmationPreview.scope || confirmationPreview.payload?.scope,
          affected_delivery_ids: confirmationPreview.affected_delivery_ids,
        }
      );
      setConfirmationPreview(null);
      setActionPreview(null);
      await loadTools();
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.message || payload?.error || 'Backend execution endpoint is not available or rejected the action.');
    } finally {
      setExecutingAction(false);
    }
  }

  async function handleImprove() {
    if (!draft.trim()) return;
    setLoadingAI(true);
    setError(null);
    try {
      const result = await improveConversationAIMessage(conversationId, draft.trim());
      setDraft(result.content || draft);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo mejorar el texto.');
    } finally {
      setLoadingAI(false);
    }
  }

  async function handleAcceptDraft() {
    if (!pendingDraft || !aiDraftText.trim()) return;
    setSending(true);
    setError(null);
    try {
      await acceptConversationAIDraft(conversationId, pendingDraft.id, aiDraftText.trim());
      await loadTools();
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.message || payload?.error || 'No se pudo enviar el borrador AI.');
    } finally {
      setSending(false);
    }
  }

  async function handleMode(mode) {
    await updateConversationAISettings(conversationId, { mode });
    await loadTools();
  }

  async function handleHandoff() {
    await requireConversationHandoff(conversationId, 'Marcado manualmente desde Communication Hub.');
    await loadTools();
  }

  async function handleResolveAsExternal() {
    setLoadingAI(true);
    setError(null);
    try {
      const external = participants.find(item => item.participant_type === 'external_whatsapp');
      await resolveConversationIdentity(conversationId, {
        participant_type: 'external_whatsapp',
        phone_number: external?.phone_number || identity.phone_number,
        role_label: 'WhatsApp verificado por admin',
        identity_metadata: identity,
        match_confidence: 'verified',
      });
      await loadTools();
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo resolver la identidad.');
    } finally {
      setLoadingAI(false);
    }
  }

  async function handleLinkDelivery() {
    const id = Number(deliveryLinkId);
    if (!id) return;
    setLoadingAI(true);
    setError(null);
    try {
      const result = await linkConversationDelivery(conversationId, id);
      setDeliveryLinkId('');
      setAIState(prev => ({ ...(prev || {}), context: { ...(prev?.context || {}), delivery: result.conversation?.delivery || prev?.context?.delivery } }));
      await loadTools();
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo vincular la entrega.');
    } finally {
      setLoadingAI(false);
    }
  }

  async function handleLeadAIMode() {
    if (!lead) return;
    setLoadingAI(true);
    setError(null);
    try {
      const result = await updateProspectLeadAIMode(lead.id, !lead.ai_auto_enabled);
      setLead(result.lead || null);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo actualizar el AI comercial del lead.');
    } finally {
      setLoadingAI(false);
    }
  }

  async function handleLeadRegistrationLink() {
    if (!lead) return;
    setSending(true);
    setError(null);
    try {
      const result = await sendProspectRegistrationLink(lead.id);
      setLead(result.lead || lead);
      await loadTools();
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo enviar el link de registro.');
    } finally {
      setSending(false);
    }
  }

  async function handleLeadAutoReply() {
    if (!lead) return;
    setSending(true);
    setError(null);
    try {
      const result = await triggerProspectAutoReply(lead.id);
      setLead(result.lead || lead);
      await loadTools();
    } catch (err) {
      const payload = err?.response?.data;
      setError(payload?.reason || payload?.error || 'El agente no pudo responder automaticamente.');
    } finally {
      setSending(false);
    }
  }

  async function handleLeadStage(stage, status = stage) {
    if (!lead) return;
    setLoadingAI(true);
    setError(null);
    try {
      const result = stage === 'converted'
        ? await markProspectConverted(lead.id)
        : await updateProspectLeadStage(lead.id, { stage, status });
      setLead(result.lead || null);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo actualizar el prospecto.');
    } finally {
      setLoadingAI(false);
    }
  }

  if (!conversationId) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500">
        Selecciona una conversacion
      </div>
    );
  }

  return (
    <>
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="flex min-h-0 flex-col gap-3">
        <ConversationThread
          conversationId={conversationId}
          latestFirst
          refreshSignal={threadRefreshSignal}
          currentUserId={userId}
          currentUserType="admin"
          className="min-h-0 flex-1"
          showInput={false}
        />

        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {CHANNELS.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  disabled={item.id === 'whatsapp' && whatsappFreeformBlocked}
                  onClick={() => {
                    if (item.id === 'whatsapp' && whatsappFreeformBlocked) return;
                    setChannel(item.id);
                    if (item.id === 'template' || item.id === 'note') setMediaAttachment(null);
                  }}
                  title={item.id === 'whatsapp' && whatsappFreeformBlocked ? whatsappDisabledReason : item.label}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 ${
                    channel === item.id ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {item.label}
                </button>
              );
            })}
            <button
              onClick={handleGenerateDraft}
              disabled={loadingAI}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-purple-100 px-3 py-2 text-xs font-bold text-purple-700 hover:bg-purple-200 disabled:opacity-50"
            >
              <Bot className="h-3.5 w-3.5" />
              AI draft
            </button>
            <button
              onClick={handleImprove}
              disabled={loadingAI || !draft.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-100 px-3 py-2 text-xs font-bold text-sky-700 hover:bg-sky-200 disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Corregir
            </button>
          </div>

          {channel === 'template' ? (
            <div className="grid gap-2 md:grid-cols-3">
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => handleTemplate(template.id)}
                  disabled={!template.available || sending}
                  className="rounded-lg border border-slate-200 p-3 text-left text-xs hover:border-[#B1336E] disabled:opacity-50"
                  title={template.disabled_reason || template.description}
                >
                  <div className="font-extrabold text-slate-900">{template.label}</div>
                  <div className="mt-1 text-slate-500">{template.disabled_reason || template.description}</div>
                  {template.selected_meta_template && (
                    <div className="mt-1 text-[10px] font-semibold text-emerald-700">{template.selected_meta_template}</div>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {channel !== 'note' && (
                <MediaMessageControls
                  value={mediaAttachment}
                  onChange={setMediaAttachment}
                  disabled={sending || (channel === 'whatsapp' && whatsappFreeformBlocked)}
                  compact
                />
              )}
              <div className="flex gap-2">
                <textarea
                  value={draft}
                  onChange={event => setDraft(event.target.value)}
                  onKeyDown={handleComposerKeyDown}
                  disabled={channel === 'whatsapp' && whatsappFreeformBlocked}
                  rows={3}
                  placeholder={channel === 'whatsapp' ? 'Responder por WhatsApp...' : channel === 'note' ? 'Nota visible solo para admins...' : 'Mensaje interno para operaciones...'}
                  className="min-h-[80px] flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
                />
                <button
                  onClick={handleSend}
                  disabled={sending || (channel === 'whatsapp' && whatsappFreeformBlocked) || (!draft.trim() && !mediaAttachment)}
                  className="self-end inline-flex items-center gap-2 rounded-xl bg-[#B1336E] px-4 py-2 text-sm font-bold text-white hover:bg-[#9d2d62] disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  Enviar
                </button>
              </div>
            </div>
          )}

          {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">{error}</div>}
          {channel === 'whatsapp' && (
            <div className="mt-2 text-[11px] text-slate-500">
              {whatsappFreeformBlocked
                ? whatsappDisabledReason
                : 'WhatsApp libre respeta la ventana de 24h. Si esta cerrada, usa una plantilla aprobada.'}
            </div>
          )}
        </div>
      </div>

      <aside className="min-h-0 space-y-3 overflow-y-auto">
        {lead && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-emerald-700" />
                  <h3 className="text-sm font-extrabold text-emerald-950">Prospecto comercial</h3>
                </div>
                <p className="mt-1 text-xs text-emerald-800">
                  Lead de WhatsApp ads separado del flujo operacional normal.
                </p>
              </div>
              <span className={`rounded-full px-2 py-1 text-[10px] font-black ${
                lead.ai_auto_enabled ? 'bg-emerald-700 text-white' : 'bg-white text-slate-600'
              }`}>
                {lead.ai_auto_enabled ? 'AI ON' : 'AI OFF'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field('Telefono', lead.phone_number)}
              {field('Stage', lead.stage)}
              {field('Intent', lead.current_intent)}
              {field('Score', `${lead.qualification_label || 'cold'} ${lead.qualification_score || 0}`)}
            </div>
            {lead.ai_summary && (
              <div className="mt-2 rounded-lg bg-white/80 p-2 text-xs font-semibold text-emerald-950">
                {lead.ai_summary}
              </div>
            )}
            {lead.requires_human && (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs font-semibold text-amber-800">
                {lead.handoff_reason || 'Requiere atencion humana.'}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={handleLeadAIMode}
                disabled={loadingAI}
                className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
              >
                {lead.ai_auto_enabled ? 'Apagar AI lead' : 'Prender AI lead'}
              </button>
              <button
                onClick={handleLeadAutoReply}
                disabled={sending}
                className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-purple-700 hover:bg-purple-50 disabled:opacity-50"
              >
                Auto-responder
              </button>
              <button
                onClick={handleLeadRegistrationLink}
                disabled={sending}
                className="rounded-lg bg-[#B1336E] px-3 py-2 text-xs font-bold text-white hover:bg-[#9d2d62] disabled:opacity-50"
              >
                Enviar registro
              </button>
              <button
                onClick={() => handleLeadStage('converted', 'converted')}
                disabled={loadingAI}
                className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                Convertido
              </button>
              <button
                onClick={() => handleLeadStage('handoff', 'handoff')}
                disabled={loadingAI}
                className="rounded-lg bg-amber-100 px-3 py-2 text-xs font-bold text-amber-800 hover:bg-amber-200 disabled:opacity-50"
              >
                Handoff
              </button>
              <button
                onClick={() => handleLeadStage('closed_lost', 'closed_lost')}
                disabled={loadingAI}
                className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 disabled:opacity-50"
              >
                Perdido
              </button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3">
            <h3 className="text-sm font-extrabold text-slate-900">AI triage chips</h3>
            <p className="text-xs text-slate-500">Urgencia operacional y estado AI visible en un vistazo.</p>
          </div>
          <AITriageChips
            conversation={conversation}
            triage={triage}
            settings={settings}
            identity={identity}
            participants={participants}
            delivery={delivery}
            pendingDraft={pendingDraft}
            recommendedActions={recommendedActions}
          />
        </div>

        <DriverIncidentPanel
          delivery={delivery}
          routeContext={routeContext}
          latestIntent={latestIntent}
          incidentResolution={incidentResolution}
        />

        <CustomerNotAnsweringPanel
          isActive={latestIntent === 'customer_not_answering'}
          onPreview={handlePreviewAction}
          isLoading={loadingPreview}
        />

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-extrabold text-slate-900">Conversation Intelligence</h3>
              <p className="text-xs text-slate-500">Resumen, riesgo y siguiente paso</p>
            </div>
            <button onClick={handleRefreshIntelligence} disabled={loadingAI} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loadingAI ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {intelligence ? (
            <div className="space-y-2 text-xs">
              <div className="rounded-lg bg-slate-50 p-2">
                <p className="font-bold text-slate-900">{intelligence.situation_summary}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {field('Riesgo', intelligence.risk_level)}
                {field('Analizado', intelligence.ai_last_analyzed_at ? new Date(intelligence.ai_last_analyzed_at).toLocaleString('es-PE') : 'live')}
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-800">
                <p className="text-[10px] font-extrabold uppercase">Siguiente paso</p>
                <p className="font-semibold">{intelligence.recommended_next_step}</p>
              </div>
              {(intelligence.missing_info || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800">
                  <p className="text-[10px] font-extrabold uppercase">Falta informacion</p>
                  <ul className="mt-1 list-disc pl-4">
                    {intelligence.missing_info.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-500">
              Cargando inteligencia de conversacion...
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-extrabold text-slate-900">AI triage</h3>
              <p className="text-xs text-slate-500">Modo, sentimiento, intent y handoff</p>
            </div>
            <button onClick={loadTools} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200">
              <RefreshCw className={`h-4 w-4 ${loadingAI ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="mb-3 flex rounded-lg bg-slate-100 p-1">
            {['off', 'draft', 'auto'].map(mode => (
              <button
                key={mode}
                onClick={() => handleMode(mode)}
                className={`flex-1 rounded-md px-2 py-1 text-xs font-bold ${
                  (settings.mode || 'draft') === mode ? 'bg-white text-[#B1336E] shadow-sm' : 'text-slate-500'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {field('Sentiment', settings.last_sentiment || 'neutral')}
            {field('Intent', settings.last_intent)}
            {field('Confidence', settings.last_confidence != null ? `${Math.round(settings.last_confidence * 100)}%` : null)}
            {field('Modo', settings.mode || 'draft')}
          </div>
          {settings.is_handoff_required && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              {settings.handoff_reason || 'Requiere atencion humana'}
            </div>
          )}
          <button onClick={handleHandoff} className="mt-3 w-full rounded-lg bg-amber-100 px-3 py-2 text-xs font-bold text-amber-800 hover:bg-amber-200">
            Marcar handoff
          </button>
        </div>

        <RecommendedActionCards actions={recommendedActions} onPreview={handlePreviewAction} isLoading={loadingPreview} />

        <DirectParticipantChatCard
          targets={directSupportTargets}
          delivery={delivery}
          currentUserId={userId}
        />

          {actionPreview && (
            <div className="rounded-lg border border-[#B1336E]/20 bg-[#B1336E]/5 p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <p className="font-extrabold text-slate-900">{actionPreview.label || actionPreview.action_type}</p>
                <span className="font-bold text-[#B1336E]">{actionPreview.mutates ? 'mutacion' : 'preview seguro'}</span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-slate-700">{actionPreview.preview_text}</p>
              {(actionPreview.warnings || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {actionPreview.warnings.map(flag => (
                    <span key={flag} className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold text-amber-800">{flag}</span>
                  ))}
                </div>
              )}
              {(actionPreview.affected_delivery_ids || actionPreview.changed_ids || []).length > 0 && (
                <div className="mt-2 rounded-lg bg-white p-2 font-semibold text-slate-700">
                  Entregas afectadas: {(actionPreview.affected_delivery_ids || actionPreview.changed_ids || []).join(', ')}
                </div>
              )}
              {['prefill_composer', 'confirm_send_template', 'confirm_api_call'].includes(actionPreview.ui_action) || actionPreview.requires_admin_confirmation ? (
                <button onClick={applyActionPreview} className="mt-2 w-full rounded-lg bg-[#B1336E] px-3 py-2 text-xs font-bold text-white">
                  {actionPreview.ui_action === 'prefill_composer' ? 'Aplicar en composer' : 'Abrir confirmación'}
                </button>
              ) : null}
            </div>
          )}

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3">
            <h3 className="text-sm font-extrabold text-slate-900">Identidad WhatsApp</h3>
            <p className="text-xs text-slate-500">Participantes, confianza y vínculo operativo</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {field('Estado', identity.status || (conversation?.type === 'unresolved_whatsapp' ? 'unresolved' : 'resolved'))}
            {field('Confianza', identity.match_confidence)}
            {field('Método', identity.match_method)}
            {field('Teléfono', identity.phone_number)}
          </div>
          <div className="mt-3 space-y-2">
            {participants.map(participant => (
              <div key={participant.id || `${participant.participant_type}-${participant.phone_number}`} className="rounded-lg bg-slate-50 p-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-extrabold text-slate-800">{participant.participant_type}</span>
                  <span className="text-slate-400">{participant.phone_number || participant.user_id || participant.driver_id}</span>
                </div>
                <p className="text-slate-500">{participant.role_label || 'Participante Fast Max'}</p>
              </div>
            ))}
          </div>
          {conversation?.type === 'unresolved_whatsapp' && (
            <button onClick={handleResolveAsExternal} disabled={loadingAI} className="mt-3 w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white disabled:opacity-50">
              Mantener como WhatsApp externo verificado
            </button>
          )}
          <div className="mt-3 flex gap-2">
            <input
              value={deliveryLinkId}
              onChange={event => setDeliveryLinkId(event.target.value)}
              placeholder="ID entrega"
              className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-[#B1336E]"
            />
            <button onClick={handleLinkDelivery} disabled={!deliveryLinkId || loadingAI} className="rounded-lg bg-[#B1336E] px-3 py-2 text-xs font-bold text-white disabled:opacity-50">
              Vincular
            </button>
          </div>
        </div>

        {pendingDraft && (
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-extrabold text-purple-950">AI draft</h3>
              <span className="text-xs font-bold text-purple-700">
                {pendingDraft.intent} · {Math.round((pendingDraft.confidence || 0) * 100)}%
              </span>
            </div>
            <textarea
              value={aiDraftText}
              onChange={event => setAiDraftText(event.target.value)}
              rows={5}
              className="w-full resize-none rounded-lg border border-purple-200 bg-white p-2 text-sm outline-none focus:border-purple-400"
            />
            {(pendingDraft.safety_flags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {pendingDraft.safety_flags.map(flag => (
                  <span key={flag} className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold text-amber-800">{flag}</span>
                ))}
              </div>
            )}
            {(pendingDraft.recommended_actions || []).length > 0 && (
              <div className="mt-2 space-y-1">
                {pendingDraft.recommended_actions.map((action, index) => (
                  <div key={`${action.type || 'action'}-${index}`} className="rounded-lg bg-white/80 p-2 text-xs text-purple-900">
                    <p className="font-extrabold">{action.label || action.type}</p>
                    <p>{action.reason}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2 grid grid-cols-3 gap-2">
              <button onClick={handleAcceptDraft} disabled={sending} className="rounded-lg bg-[#B1336E] px-2 py-2 text-xs font-bold text-white disabled:opacity-50">
                Enviar WA
              </button>
              <button onClick={() => { setDraft(aiDraftText); setChannel('whatsapp'); }} className="rounded-lg bg-white px-2 py-2 text-xs font-bold text-purple-700">
                Usar
              </button>
              <button onClick={() => dismissConversationAIDraft(conversationId, pendingDraft.id).then(loadTools)} className="rounded-lg bg-white px-2 py-2 text-xs font-bold text-slate-600">
                Descartar
              </button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-extrabold text-slate-900">Delivery context</h3>
              <p className="text-xs text-slate-500">Datos usados por AI y operaciones</p>
            </div>
            {delivery?.id && <ChatViewHelpers deliveryPointID={delivery.id} />}
          </div>
          {!delivery?.found ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              No se encontro entrega vinculada.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {field('ID', delivery.id)}
              {field('Estado', delivery.status)}
              {field('Cliente', delivery.recipient_name)}
              {field('Fecha / horario', [delivery.delivery_date, delivery.slot_label].filter(Boolean).join(' · '))}
              {field('Distrito', delivery.district)}
              {field('Monto', delivery.charge != null ? `S/ ${Number(delivery.charge).toFixed(2)}` : null)}
              {field('Repartidor', delivery.driver_name)}
              {field('Ubicacion', delivery.has_location ? 'Registrada' : 'Falta')}
              <div className="col-span-2">{field('Direccion', delivery.address)}</div>
              {delivery.tracking_url && (
                <a className="col-span-2 rounded-lg bg-slate-900 px-3 py-2 text-center text-xs font-bold text-white" href={delivery.tracking_url} target="_blank" rel="noreferrer">
                  Ver tracking
                </a>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
    <ActionConfirmationModal
      preview={confirmationPreview}
      isExecuting={executingAction}
      error={error}
      onCancel={() => setConfirmationPreview(null)}
      onConfirm={handleConfirmAction}
    />
    </>
  );
}
